/**
 * localStorage wrapper that never throws. When storage is unavailable (private browsing,
 * disabled cookies, quota errors) it falls back to an in-memory map and reports that state so
 * the UI can explain that a refresh may lose data.
 */
export interface SafeStorage {
  readonly persistent: boolean
  get<T>(key: string): T | null
  set(key: string, value: unknown): boolean
  remove(key: string): void
  keys(prefix: string): string[]
}

export function createSafeStorage(backing: Storage | null = detectStorage()): SafeStorage {
  const memory = new Map<string, string>()
  let persistent = backing !== null

  const read = (key: string): string | null => {
    if (persistent && backing) {
      try {
        return backing.getItem(key)
      } catch {
        persistent = false
      }
    }
    return memory.get(key) ?? null
  }

  return {
    get persistent() {
      return persistent
    },
    get<T>(key: string): T | null {
      const raw = read(key)
      if (raw === null) return null
      try {
        return JSON.parse(raw) as T
      } catch {
        return null
      }
    },
    set(key: string, value: unknown): boolean {
      const raw = JSON.stringify(value)
      if (persistent && backing) {
        try {
          backing.setItem(key, raw)
          return true
        } catch {
          persistent = false
        }
      }
      memory.set(key, raw)
      return false
    },
    remove(key: string): void {
      memory.delete(key)
      if (persistent && backing) {
        try {
          backing.removeItem(key)
        } catch {
          persistent = false
        }
      }
    },
    keys(prefix: string): string[] {
      const found = new Set([...memory.keys()].filter((k) => k.startsWith(prefix)))
      if (persistent && backing) {
        try {
          for (let i = 0; i < backing.length; i++) {
            const key = backing.key(i)
            if (key?.startsWith(prefix)) found.add(key)
          }
        } catch {
          persistent = false
        }
      }
      return [...found]
    },
  }
}

export function detectStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    const storage = window.localStorage
    const probe = '__ls_probe__'
    storage.setItem(probe, '1')
    storage.removeItem(probe)
    return storage
  } catch {
    return null
  }
}
